import { useCallback } from 'react';
import useApi from '../hooks/useApi';

/**
 * Centro de incidentes (backend `nuatalker-backend-app`, lambda `incidents`):
 *   GET /incidents                                    lista
 *   GET /incidents/summary                            resumen por integración
 *   GET /incidents/{clientId}/{integration}/{cause}   detalle con muestras
 * Solo las puede llamar el grupo `backoffice`; para cualquier otro usuario el API
 * responde 403 y aquí llega como un error con ese mensaje.
 */

const asList = (value) => (Array.isArray(value) ? value : []);

const compactQuery = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value != null && value !== '')
  );

export const normalizeIncident = (row = {}) => ({
  id: row.id || `${row.key || ''}#${row.firstSeenAt || ''}`,
  integration: row.integration || '',
  clientId: row.clientId == null ? '' : String(row.clientId),
  cause: row.cause || '',
  status: row.status || '',
  firstSeenAt: row.firstSeenAt || null,
  openedAt: row.openedAt || null,
  lastFailureAt: row.lastFailureAt || null,
  lastSuccessAt: row.lastSuccessAt || null,
  resolvedAt: row.resolvedAt || null,
  attempts: Number(row.attempts) || 0,
  failures: Number(row.failures) || 0,
  successes: Number(row.successes) || 0,
  updatedAt: row.updatedAt || null,
});

export const normalizeIncidentList = (response) => ({
  items: asList(response?.items).map(normalizeIncident),
  nextToken: response?.nextToken || null,
});

export const normalizeSummary = (response) => ({
  generatedAt: response?.generatedAt || null,
  resolvedWindowDays: Number(response?.resolvedWindowDays) || 30,
  totals: {
    open: Number(response?.totals?.open) || 0,
    resolved: Number(response?.totals?.resolved) || 0,
  },
  integrations: asList(response?.integrations).map((row) => ({
    integration: row.integration || '',
    open: Number(row.open) || 0,
    resolved: Number(row.resolved) || 0,
    clientsAffected: Number(row.clientsAffected) || 0,
    lastUpdatedAt: row.lastUpdatedAt || null,
  })),
});

export const normalizeIncidentDetail = (response) => ({
  incident: normalizeIncident(response?.incident),
  samples: asList(response?.samples).map((row) => ({
    at: row.at || row.sample?.at || null,
    signalId: row.signalId || '',
    change: row.change || '',
    sample: row.sample || null,
  })),
  episodes: asList(response?.episodes).map((row) => ({
    firstSeenAt: row.firstSeenAt || null,
    status: row.status || '',
    openedAt: row.openedAt || null,
    resolvedAt: row.resolvedAt || null,
    failures: Number(row.failures) || 0,
  })),
});

export const useIncidentsService = () => {
  const { callApi } = useApi();

  const call = useCallback(
    async (path, queryParams, fallbackError) => {
      try {
        const data = await callApi({
          apiName: 'nuatalker',
          path,
          method: 'get',
          queryParams: queryParams ? compactQuery(queryParams) : undefined,
        });
        return { success: true, data };
      } catch (error) {
        return {
          success: false,
          status: error?.status,
          error: error?.message || fallbackError,
        };
      }
    },
    [callApi]
  );

  const listIncidents = useCallback(
    async ({ status = 'open', integration, clientId, since, limit = 50, nextToken } = {}) => {
      const result = await call(
        '/incidents',
        { status, integration, clientId, since, nextToken, limit: String(limit) },
        'Error al obtener los incidentes'
      );
      return result.success
        ? { success: true, data: normalizeIncidentList(result.data) }
        : result;
    },
    [call]
  );

  const getIncidentsSummary = useCallback(
    async ({ days } = {}) => {
      const result = await call(
        '/incidents/summary',
        { days: days ? String(days) : undefined },
        'Error al obtener el resumen de incidentes'
      );
      return result.success
        ? { success: true, data: normalizeSummary(result.data) }
        : result;
    },
    [call]
  );

  const getIncidentDetail = useCallback(
    async ({ clientId, integration, cause, firstSeenAt, samples = 50 }) => {
      const path = `/incidents/${encodeURIComponent(clientId)}/${encodeURIComponent(
        integration
      )}/${encodeURIComponent(cause)}`;
      const result = await call(
        path,
        { firstSeenAt, samples: String(samples) },
        'Error al obtener el detalle del incidente'
      );
      return result.success
        ? { success: true, data: normalizeIncidentDetail(result.data) }
        : result;
    },
    [call]
  );

  return { listIncidents, getIncidentsSummary, getIncidentDetail };
};

export default useIncidentsService;
